"use client";

import { motion } from "framer-motion";
import { ArrowRight, Layers, Users, Workflow, SlidersHorizontal } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const institutionalReasons = [{
  index: "01",
  label: "Cohort Scale",
  icon: Layers,
  title: "Diagnostic patterns emerge at cohort scale",
  description: "Patterns at the method-step level become clear across an entire cohort's assessment data. Examining single students in isolation conceals the systemic points of failure that appear repeatedly across cohorts."
}, {
  index: "02",
  label: "Actionable Pedagogy",
  icon: SlidersHorizontal,
  title: "Teachers can adjust instructional delivery",
  description: "Teachers are directly situated to act on diagnosis by adjusting how they teach a topic or scaffold a step. A tool sold directly to families lacks the classroom practitioner who can implement instructional adjustments."
}, {
  index: "03",
  label: "Native Integration",
  icon: Workflow,
  title: "Setura reads existing student work",
  description: "Setura analyses work students are already producing for school, routine classwork, homework tasks, and formal mocks. By embedding directly inside the school's existing workflows, it requires zero extra burden from families."
}, {
  index: "04",
  label: "Departmental Scale",
  icon: Users,
  title: "One partnership reaches an entire department",
  description: "A single partnership with a school deploys across a whole year group or department. This creates immediate, consistent diagnostic coverage without needing separate trust and individual adoption from every household."
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function WhySchools() {
  return <section id="whyschools" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 lg:py-32 border-b border-border/70 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 mb-4 font-mono text-xs uppercase tracking-widest text-primary/80 bg-muted px-2.5 py-1 rounded-sm border border-border/60">
            <span>01</span>
            <span className="opacity-40">//</span>
            <span>Institutional Focus</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-semibold tracking-tight text-foreground text-balance">
            Why we work with schools
          </h2>

          <p className="mt-4 sm:mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed text-balance">
            Accurate diagnosis of process-level misconceptions requires institutional context. We partner directly with schools and departments to make diagnostic insight immediate, systematic, and actionable in the classroom.
          </p>
        </div>

        {/* 2x2 Grid of Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 lg:gap-8">
          {institutionalReasons.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.index} variants={cardVariants} data-index={i}>
                <Card className="h-full rounded-sm border border-border/80 bg-card hover:border-primary/40 transition-colors duration-200 py-6 sm:py-7">
                  <CardHeader className="px-6 sm:px-7 pb-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                        {item.index} // {item.label}
                      </span>
                      <div className="size-8 rounded-sm bg-muted flex items-center justify-center text-primary border border-border/50">
                        <Icon className="size-4" aria-hidden="true" />
                      </div>
                    </div>
                    <CardTitle className="text-lg sm:text-xl font-semibold tracking-tight text-card-foreground leading-snug pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-6 sm:px-7 pt-1">
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Footer Link / Action */}
        <div className="mt-12 sm:mt-16 pt-8 border-t border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Explore how Lumina and Setura deploy across your school's existing assessments.
          </p>
          <Button asChild variant="outline" size="default" className="rounded-sm border-border hover:border-primary/60 text-foreground group w-fit">
            <Link to="/PilotPage" className="inline-flex items-center gap-2">
              <span>Start a conversation</span>
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}